import { type FC } from 'react';
import {
  Chip,
  ListItemButton,
  ListItemText,
  ListItem as MuiListItem,
  Stack,
  Tooltip,
  Typography,
  type ListItemProps,
} from '@mui/material';
import { NavLink } from 'react-router';
import { FolderCopy, LocationPin, Schedule } from '@mui/icons-material';

import { isNil } from '#packages/data-collection/libs/lodash';
import { type Audit } from '#packages/data-collection/models/audit';

import { AuditTypeChip } from '#apps/client-web/features/audit/components/audit-type-chip';

export type AuditListItemProps = ListItemProps & {
  /**
   * Audit
   */
  audit: Audit;

  /**
   * Link to navigate to when the item is clicked
   */
  detailsPageLink: string;
};

export const AuditListItem: FC<AuditListItemProps> = ({
  audit,
  detailsPageLink,

  ...props
}) => (
  <MuiListItem divider={true} {...props}>
    <ListItemButton component={NavLink} to={detailsPageLink}>
      <ListItemText
        primary={audit.title}
        secondary={
          <Stack sx={{ mt: 0.5 }} spacing={1}>
            {isNil(audit.address) === false && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <LocationPin />
                <Typography variant="caption">
                  {audit.address.toString()}
                </Typography>
              </Stack>
            )}
            <Stack direction="row" spacing={1}>
              <Tooltip title={audit.updatedAt.format('l LT')}>
                <Chip
                  icon={<Schedule />}
                  label={audit.updatedAt.fromNow()}
                  variant="outlined"
                  size="small"
                />
              </Tooltip>
              {!isNil(audit.project) && (
                <Tooltip title="Project">
                  <Chip
                    icon={<FolderCopy />}
                    label={audit.project}
                    variant="outlined"
                    size="small"
                    sx={{ px: 0.5 }}
                  />
                </Tooltip>
              )}
              {audit.types.map((type) => (
                <AuditTypeChip key={type} type={type} size="small" />
              ))}
            </Stack>
          </Stack>
        }
        slotProps={{
          primary: {
            sx: {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          },
          secondary: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      />
    </ListItemButton>
  </MuiListItem>
);
